import React from 'react';
import { Notification } from '../../types/notification';
import NotificationItem from './NotificationItem';
import LegacyNotificationItem from './LegacyNotificationItem';
import { Button, Loading, EmptyState } from '../UI';
import IconSVG from '../UI/IconSVG';

interface NotificationListProps {
  notifications: Notification[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead?: () => void;
  onDelete: (id: string) => void;
  className?: string;
}

const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  isLoading,
  hasMore,
  onLoadMore,
  onMarkAsRead,
  onDelete,
  className = '',
}) => {

  return (
    <div className={`bg-white dark:bg-gray-800 flex flex-col ${className}`}>
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        {/* Empty state */}
        {!isLoading && notifications.length === 0 && (
          <EmptyState
            icon="info"
            title="No notifications"
            message="You don't have any notifications yet. They will appear here when you receive them."
            className="py-10"
          />
        )}

        {/* Notifications list */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {notifications.map((notification) => {
          // Check if this is a legacy notification
          const isLegacy = 'eventInitiator' in notification || 
                          'isRead' in notification || 
                          'timestamp' in notification ||
                          notification.type === 'requestReceived' ||
                          notification.type === 'requestAccepted' ||
                          notification.type === 'oss' ||
                          notification.type === 'friend-belt-prom' ||
                          notification.type === 'perk-unlocked';
          
          if (isLegacy) {
            return (
              <LegacyNotificationItem
                key={notification.id || notification.key}
                notification={notification}
                onMarkAsRead={onMarkAsRead}
                onDelete={onDelete}
              />
            );
          }
          
          return (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={onMarkAsRead}
              onDelete={onDelete}
            />
          );
        })}
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-center items-center py-6">
            <Loading size="md" />
          </div>
        )}

        {/* Load more button */}
        {!isLoading && hasMore && notifications.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadMore}
              className="text-gray-600 dark:text-gray-300"
            >
              <IconSVG icon="refresh" className="mr-1" />
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationList;